import * as React from 'react'
import { Dispatch, ReactElement, SetStateAction, useEffect, useMemo, useState } from 'react'
import { Card, Divider, makeStyles, shorthands, Tab, TabList, TabValue, Text, tokens } from '@fluentui/react-components'

import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { InvoiceApprovalDto, InvoiceApprovalStatus, UserInvoiceApprovalDto } from './InvoiceApprovalOverview'
import Form from '../../components/Form/Form'
import useEntity from '../../hooks/useEntity'
import { Formik, useFormikContext } from 'formik'
import useUser from '../../hooks/useUser'
import * as UUID from 'uuid'
import { useHistory, useParams } from 'react-router-dom'
import { SummaryDto } from '../../config/fileConfig'
import PersonAvatar from '../../components/PersonAvatar'
import useAxios from '../../hooks/useAxios'
import useUsers from '../../hooks/useUsers'
import { ExtendedTimeOverviewDto } from '../Time/BaseTimeOverview'
import { FilePartyMetaDataDto, InvoiceMutationDto, InvoiceTemplateDto, LineItemGroupType, LineItemType } from '../../config/finance/invoiceConfig'
import { defaultInvoiceValues } from '../Finance/Invoice/AddInvoice'
import { sortLineItemGroups } from '../Finance/Invoice/EditInvoice'
import { PricePrecedence } from '../../config/finance/financeConfig'
import { UserInvoiceApproval } from './UserInvoiceApproval'
import InvoiceApprovalHeader from './InvoiceApprovalHeader'
import useRoles, { Role } from '../../hooks/useRoles'
import * as Yup from 'yup'
import ViewInvoiceApprovalTimes from './ViewInvoiceApprovalTimes'
import { DataPromise } from '../../context/AxiosContext'
import { ExtendedTravelOverviewDto } from '../Travel/BaseTravelOverview'
import ViewInvoiceApprovalTravels from './ViewInvoiceApprovalTravels'
import { DisbursementIcon, FixedPriceIcon, TimerIcon, TravelIcon } from '../../components/MainMenu'
import ViewInvoiceApprovalDisbursements from './ViewInvoiceApprovalDisbursements'
import ViewInvoiceApprovalFixedPrices from './ViewInvoiceApprovalFixedPrices'
import { ExtendedDisbursementOverviewDto } from '../Disbursement/BaseDisbursementOverview'
import { ExtendedFixedPriceOverviewDto } from '../FixedPrice/BaseFixedPriceOverview'
import { FixedPriceType } from '../../config/registrationConfig'
import { usePrevious } from '@fluentui/react-hooks'

export const useInvoiceApprovalStyles = makeStyles({
    cardMargin: {
        ...shorthands.margin(tokens.spacingVerticalS, tokens.spacingHorizontalS),
        columnGap: tokens.spacingHorizontalS,
        rowGap: tokens.spacingVerticalS,
    },
    cardSingleColumnGrid: {
        columnGap: tokens.spacingHorizontalS,
        rowGap: tokens.spacingVerticalS,
        display: 'grid',
        gridTemplateColumns: '1fr',

        '> div > div > span': {
            ...shorthands.padding(tokens.spacingVerticalXS),
        },
        '> div > div > div': {
            ...shorthands.padding(tokens.spacingVerticalXS),
        },
    },
    cardDualColumnGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        columnGap: tokens.spacingHorizontalL,
    },
    accordion: {
        display: 'grid',
        ...shorthands.gap(tokens.spacingVerticalL, tokens.spacingHorizontalS),
    },
    accordionPanel: {
        display: 'grid',
        gridTemplateColumns: '50% 40%',
        ...shorthands.gap(tokens.spacingVerticalL, tokens.spacingHorizontalS),
    },
    accordionContent: {
        display: 'grid',
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        '> *': {
            display: 'flex',
            ...shorthands.gap(tokens.spacingHorizontalS, tokens.spacingHorizontalS),
        },
    },
    validationsContainer: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingHorizontalS, tokens.spacingHorizontalS),
    },
    userValidationContainer: {
        display: 'grid',
        gridTemplateColumns: '80px minmax(400px, max-content) 200px',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
    },
    userValidationSmallContainer: {
        gridTemplateColumns: '80px minmax(400px, max-content)',
        gridTemplateRows: 'minmax(100px, max-content) 1fr',
    },
    userValidationIconContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
    },
    userValidationIconSmallContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
    },
    userValidationComment: {
        minWidth: '200px',
        width: '350px',
        minHeight: '100px',
        height: '100px',

        '> textarea:disabled': {
            color: tokens.colorNeutralForeground3,
        },
    },
    userValidationActionContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
    },
    userValidationActionSmallContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
    },

    dataGridsRoot: {
        alignItems: 'flex-start',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        // padding: '50px 20px',
        rowGap: '20px',
    },
    hidden: {
        display: 'none',
    },
    centerItems: {
        alignSelf: 'center',
        textAlign: 'center',
        fontSize: tokens.fontSizeBase300,
    },
    flexGap: {
        display: 'flex',
        gap: tokens.spacingHorizontalS,
    },
    flexWrap: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    flexJustifyBetween: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    warning: {
        color: tokens.colorStatusWarningBackground3,
    },
})

const getValidationSchema = (translate: (key: string) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        debtorId: Yup.string().required().label(translate('invoiceapproval.debtor.label')),
        clientId: Yup.string().required().label(translate('invoiceapproval.client.label')),
        invoiceReference: Yup.string()
            .nullable()
            .test({
                message: translate('invoice.edit.form.header.recipientreference.required'),
                test: (value, context) => {
                    const references = value?.trim()?.split(',')
                    const fileIds = context.parent.fileIds?.length

                    return !context.parent.invoiceReferenceRequired || references?.length === fileIds
                },
            }),
    })
}

export const createInvoiceFromApproval = (
    values: InvoiceApprovalMutationDto,
    approval: InvoiceApprovalDto,
    invoiceTemplate: InvoiceTemplateDto,
    getFixedPriceType: (fixedPriceType: string) => FixedPriceType,
    fixedPrices?: ExtendedFixedPriceOverviewDto[],
): Partial<InvoiceMutationDto> => {
    return {
        ...defaultInvoiceValues(),
        ...invoiceTemplate,
        id: undefined,
        invoiceApprovalId: approval.id,
        fileIds: values.fileIds,
        templateId: invoiceTemplate.id,
        lineItemGroups: approval.files
            ?.flatMap(file => {
                return invoiceTemplate.lineItemGroups.map(groupType => {
                    const fixedPrice = fixedPrices?.find(fixedPrice =>
                        getFixedPriceType(fixedPrice.type)?.prefillType?.includes(groupType.prefillType),
                    )
                    const fixedPriceType = fixedPrice ? getFixedPriceType(fixedPrice.type) : undefined

                    return {
                        ...groupType,
                        id: UUID.v4(),
                        templateGroupId: groupType.id,
                        fileId: file.id,
                        fixedPriceId: fixedPrice?.id,
                        file: { ...file, ownerId: file.owner.id },
                        isNew: true,
                        precedence: fixedPrice ? PricePrecedence.Subtotal : (groupType.precedence ?? PricePrecedence.Inherited),
                        description: fixedPrice?.description ?? groupType.description,
                        price: fixedPrice?.rate.amount ?? groupType.price,
                        vatType: fixedPrice?.vatType ?? groupType.vatType,
                        showStatement: fixedPriceType?.showStatement ?? groupType.showStatement,
                        lineItems: getLineItems(groupType, approval),
                    }
                })
            })
            .filter(group => group.lineItems.length > 0)
            .filter(Boolean)
            .sort(sortLineItemGroups),
        debtorPivotId: values.debtorId,
        clientPivotId: values.clientId,
        recipientEmail: values.invoiceEmail,
        recipientReference: values.invoiceReference,
    }
}

const getLineItems = (groupType: LineItemGroupType, approval: InvoiceApprovalDto) => {
    switch (groupType.prefillType) {
        case LineItemType.Disbursement:
            return approval.disbursements.map(item => ({
                type: LineItemType.Disbursement,
                entityId: item,
            }))
        case LineItemType.FixedPrice:
            return approval.fixedPrices.map(item => ({
                type: LineItemType.FixedPrice,
                entityId: item,
            }))
        case LineItemType.Time:
            return approval.times.map(item => ({
                type: LineItemType.Time,
                entityId: item,
            }))
        case LineItemType.Travel:
            return approval.travels.map(item => ({
                type: LineItemType.Travel,
                entityId: item,
            }))
        default:
            return []
    }
}

//The Dto used to fill the form. Submitting data differs from older pages.
export type InvoiceApprovalMutationDto = {
    id?: string
    userId: string
    fileIds: string[]
    clientId?: string
    debtorId?: string
    invoiceEmail?: string
    invoiceReference?: string
    invoiceReferenceRequired?: boolean //used locally only
    userApprovals: UserInvoiceApprovalMutationDto[]
    disbursements: string[]
    fixedPrices: string[]
    times: string[]
    travels: string[]
}

//The Dto used to fill the form. Submitting data differs from older pages.
export type UserInvoiceApprovalMutationDto = Partial<UserInvoiceApprovalDto>

export type InvoiceApprovalMetaDataDto = {
    files?: SummaryDto[]
    debtor?: FilePartyMetaDataDto
    client?: FilePartyMetaDataDto
    fileParties?: FilePartyMetaDataDto[]
}

export type ViewInvoiceApprovalProps = {
    headerOrientation: 'horizontal' | 'vertical'
    orientation: 'horizontal' | 'vertical'

    approval?: InvoiceApprovalDto
}

export type ViewInvoiceApprovalComponentProps = ViewInvoiceApprovalProps & {
    hiddenClassName?: string

    meta: DataPromise<InvoiceApprovalMetaDataDto>
    invoiceTemplate: InvoiceTemplateDto

    approvalStatus: InvoiceApprovalStatus
    setApprovalStatus: Dispatch<SetStateAction<InvoiceApprovalStatus>>

    isApprovalActive: boolean
    isFileOwnerOrAdmin: boolean
    isFileOwnerOrAdminOrInvoicer: boolean
}

function Container(props: ViewInvoiceApprovalProps): ReactElement {
    const styles = useInvoiceApprovalStyles()
    const { any: anyRole } = useRoles()
    const { t: translate } = useTranslation()
    const { currentUser } = useUser()
    const { getUserById } = useUsers()

    const { values, status, isSubmitting, setFieldValue } = useFormikContext<InvoiceApprovalMutationDto>()
    const previousValues = usePrevious(values)

    //Update metadata whenever the number of file ids change.
    const [meta] = useEntity<InvoiceApprovalMetaDataDto>(
        () => ({
            url: values.id
                ? `/finance/invoice-approval/${values.id}/meta?fileId=${values.fileIds.join(',')}`
                : `/finance/invoice-approval/meta?fileId=${values.fileIds.join(',')}`,
        }),
        values.fileIds?.length > 0 ? `${values.fileIds?.length}` : null,
        result => result.data,
        undefined,
        undefined,
        [values.fileIds],
    )

    const [invoiceTemplate, setInvoiceTemplate] = useState<InvoiceTemplateDto>()
    const [templatesMeta] = useEntity<InvoiceTemplateDto[]>(
        () => ({ url: '/finance/invoice/template', method: 'GET' }),
        values.fileIds ? false : null,
    )

    const [approvalStatus, setApprovalStatus] = useState<InvoiceApprovalStatus>(props.approval?.status || InvoiceApprovalStatus.New)
    const file = useMemo<SummaryDto>(() => meta.data?.files?.find(file => values.fileIds.some(id => file.id === id)), [meta, values.fileIds])
    const client = useMemo(() => meta.data?.fileParties.find(party => party.pivotId === values.clientId), [meta, values.clientId])
    const debtor = useMemo(() => meta.data?.fileParties.find(party => party.pivotId === values.debtorId), [meta, values.debtorId])

    useEffect(() => {
        if (!values.id && !values.clientId) {
            setFieldValue('clientId', meta.data?.client?.pivotId)
        }
        if (!values.id && !values.debtorId) {
            setFieldValue('debtorId', meta.data?.debtor?.pivotId)
        }
    }, [meta.loaded])

    useEffect(() => {
        if (!values.id && !values.invoiceEmail) {
            setFieldValue('invoiceEmail', file?.invoiceEmail || debtor?.financeEmail || debtor?.email)
        }
        if (!values.id && !values.invoiceEmail) {
            setFieldValue('invoiceReference', debtor?.reference)
        }
        if (previousValues && !file?.invoiceEmail && previousValues.debtorId !== values.debtorId) {
            setFieldValue('invoiceEmail', debtor?.financeEmail || debtor?.email)
        }
        if (previousValues && previousValues.debtorId && previousValues.debtorId !== values.debtorId) {
            setFieldValue('invoiceReference', debtor?.reference)
        }

        setFieldValue('invoiceReferenceRequired', !!debtor?.referenceRequired)
    }, [meta.loaded, values.debtorId])

    useEffect(() => {
        if (templatesMeta.loaded) {
            setInvoiceTemplate(templatesMeta.data?.find(template => template.isDefault))
        }
    }, [templatesMeta])

    const [disbursementsCount, setDisbursementsCount] = useState<number>(0)
    const [disbursements, setDisbursements] = useState<ExtendedDisbursementOverviewDto[]>([])
    const [selectedDisbursements, setSelectedDisbursements] = useState<ExtendedDisbursementOverviewDto[]>()

    const [fixedPricesCount, setFixedPricesCount] = useState<number>(0)
    const [fixedPrices, setFixedPrices] = useState<ExtendedFixedPriceOverviewDto[]>([])
    const [selectedFixedPrices, setSelectedFixedPrices] = useState<ExtendedFixedPriceOverviewDto[]>()

    const [timesCount, setTimesCount] = useState<number>(0)
    const [times, setTimes] = useState<ExtendedTimeOverviewDto[]>([])
    const [selectedTimes, setSelectedTimes] = useState<ExtendedTimeOverviewDto[]>()

    const [travelsCount, setTravelsCount] = useState<number>(0)
    const [travels, setTravels] = useState<ExtendedTravelOverviewDto[]>([])
    const [selectedTravels, setSelectedTravels] = useState<ExtendedTravelOverviewDto[]>()

    //Initially select all registrations for a new InvoiceApproval.
    useEffect(() => {
        if (!values.id && values.disbursements?.length === 0 && disbursementsCount === disbursements.length) {
            console.log(`Initially select all disbursements for a new InvoiceApproval`)
            setFieldValue(
                'disbursements',
                disbursements?.map(time => time.id),
            )
        }
    }, [disbursements])
    useEffect(() => {
        if (!values.id && values.fixedPrices?.length === 0 && fixedPricesCount === fixedPrices.length) {
            console.log(`Initially select all fixedPrices for a new InvoiceApproval`)
            setFieldValue(
                'fixedPrices',
                fixedPrices?.map(time => time.id),
            )
        }
    }, [fixedPrices])
    useEffect(() => {
        if (!values.id && values.times?.length === 0 && timesCount === times.length) {
            console.log(`Initially select all times for a new InvoiceApproval`)
            setFieldValue(
                'times',
                times?.map(time => time.id),
            )
        }
    }, [times])
    useEffect(() => {
        if (!values.id && values.travels?.length === 0 && travelsCount === travels.length) {
            console.log(`Initially select all travels for a new InvoiceApproval`)
            setFieldValue(
                'travels',
                travels?.map(time => time.id),
            )
        }
    }, [travels])

    //Update the selected disbursements when (de)selecting disbursements.
    useEffect(() => {
        if (values && disbursements) {
            const result = disbursements?.filter(time => values?.disbursements?.includes(time.id))
            console.log(`recalculating selectedDisbursements using ${disbursements?.length} disbursements: ${result?.length}`)
            setSelectedDisbursements(result)
        } else {
            console.log(`resetting selectedDisbursements`)
            setSelectedDisbursements([])
        }
    }, [disbursements, values.disbursements])

    //Update the selected fixedPrices when (de)selecting fixedPrices.
    useEffect(() => {
        if (values && fixedPrices) {
            const result = fixedPrices?.filter(time => values?.fixedPrices?.includes(time.id))
            console.log(`recalculating selectedFixedPrices using ${fixedPrices?.length} fixedPrices: ${result?.length}`)
            setSelectedFixedPrices(result)
        } else {
            console.log(`resetting selectedFixedPrices`)
            setSelectedFixedPrices([])
        }
    }, [fixedPrices, values.fixedPrices])

    //Update the selected times when (de)selecting times.
    useEffect(() => {
        if (values && times) {
            const result = times?.filter(time => values?.times?.includes(time.id))
            console.log(`recalculating selectedTimes using ${times?.length} times: ${result?.length}`)
            setSelectedTimes(result)
        } else {
            console.log(`resetting selectedTimes`)
            setSelectedTimes([])
        }
    }, [times, values.times])

    //Update the selected travels when (de)selecting travels.
    useEffect(() => {
        if (values && travels) {
            const result = travels?.filter(time => values?.travels?.includes(time.id))
            console.log(`recalculating selectedTravels using ${travels?.length} travels: ${result?.length}`)
            setSelectedTravels(result)
        } else {
            console.log(`resetting selectedTravels`)
            setSelectedTravels([])
        }
    }, [travels, values.travels])

    //Update the selected times/userApprovals when (de)selecting times.
    useEffect(() => {
        if (!values.id) {
            let users = selectedTimes
                ?.map(time => time.user.id)
                .concat(selectedTravels?.map(time => time.user.id))
                .concat(selectedDisbursements?.map(time => time.user.id))
                .concat(selectedFixedPrices?.map(time => time.user.id))

            users = users?.filter((userId, index) => users?.lastIndexOf(userId) === index)
            setFieldValue(
                'userApprovals',
                users
                    ?.map(userId => ({
                        userId: userId,
                        fullName: getUserById(userId)?.fullName,
                        status: InvoiceApprovalStatus.New,
                    }))
                    .sort((a, b) => a.fullName?.localeCompare(b.fullName)) || [],
            )
        }
    }, [selectedDisbursements, selectedFixedPrices, selectedTimes, selectedTravels])

    const isApprovalActive = useMemo(() => [InvoiceApprovalStatus.New, InvoiceApprovalStatus.Active].includes(approvalStatus), [approvalStatus])
    const isFileOwnerOrAdmin = useMemo(
        () => anyRole([]) || props.approval?.files?.some(file => file.owner.id === currentUser.id),
        [isApprovalActive, props.approval, currentUser],
    )
    const isFileOwnerOrAdminOrInvoicer = useMemo(
        () => anyRole([Role.Invoice]) || props.approval?.files?.some(file => file.owner.id === currentUser.id),
        [isApprovalActive, props.approval, currentUser],
    )

    const [selectedTab, setSelectedTab] = useState<TabValue>('times')

    const componentProps: ViewInvoiceApprovalComponentProps = {
        ...props,
        meta,
        invoiceTemplate,
        approvalStatus,
        setApprovalStatus,
        isApprovalActive,
        isFileOwnerOrAdmin,
        isFileOwnerOrAdminOrInvoicer,
    }

    if (!selectedTimes || !invoiceTemplate) {
        return <></>
    }

    return (
        <Form status={status} noLoading={!isSubmitting}>
            <InvoiceApprovalHeader
                {...componentProps}
                file={file}
                client={client}
                debtor={debtor}
                isFileOwnerOrAdmin={isFileOwnerOrAdmin}
                isFileOwnerOrAdminOrInvoicer={isFileOwnerOrAdminOrInvoicer}
                selectedDisbursements={selectedDisbursements}
                selectedFixedPrices={selectedFixedPrices}
                selectedTimes={selectedTimes}
                selectedTravels={selectedTravels}
            />

            <Card className={styles.cardMargin}>
                <Text size={500}>{translate('invoiceapproval.userapprovals.title')}</Text>
                {values.fileIds?.length === 0 && <Text>{translate('invoiceapproval.userapprovals.selectfile')}</Text>}
                {meta.loading && <Text>{translate('base.loading')}</Text>}
                {meta.loaded && values.userApprovals?.length === 0 && <Text>{translate('invoiceapproval.userapprovals.empty')}</Text>}
                {!values.id && values.userApprovals?.length > 0 && (
                    <div className={styles.userValidationContainer}>
                        {values.userApprovals?.map((value, index) => {
                            return (
                                <div key={index}>
                                    <PersonAvatar name={getUserById(value.userId)?.fullName} color="colorful" />
                                </div>
                            )
                        })}
                    </div>
                )}
                {values.id && values.userApprovals?.length > 0 && (
                    <div className={styles.validationsContainer}>
                        {values.userApprovals
                            ?.map((value, index) => (
                                <UserInvoiceApproval
                                    key={index}
                                    index={index}
                                    {...componentProps}
                                    isApprovalActive={isApprovalActive}
                                    isFileOwnerOrAdmin={isFileOwnerOrAdmin}
                                    userApproval={value}
                                    selectedDisbursements={selectedDisbursements}
                                    selectedFixedPrices={selectedFixedPrices}
                                    selectedTimes={selectedTimes}
                                    selectedTravels={selectedTravels}
                                />
                            ))
                            .reduce(
                                (a, b) => {
                                    return (
                                        <>
                                            {a}
                                            <Divider />
                                            {b}
                                        </>
                                    )
                                },
                                <div></div>,
                            )}
                    </div>
                )}
            </Card>

            <Card className={styles.cardMargin}>
                <TabList selectedValue={selectedTab} onTabSelect={(_, data) => setSelectedTab(data.value)}>
                    <Tab id="times" icon={<TimerIcon />} value="times">
                        {translate('invoiceapproval.times.title', {
                            selectedCount: values.times?.length || 0,
                            count: timesCount,
                        })}
                    </Tab>
                    <Tab id="travels" icon={<TravelIcon />} value="travels">
                        {translate('invoiceapproval.travels.title', {
                            selectedCount: values.travels?.length || 0,
                            count: travelsCount,
                        })}
                    </Tab>
                    <Tab id="disbursements" icon={<DisbursementIcon />} value="disbursements">
                        {translate('invoiceapproval.disbursements.title', {
                            selectedCount: values.disbursements?.length || 0,
                            count: disbursementsCount,
                        })}
                    </Tab>
                    <Tab id="fixedprices" icon={<FixedPriceIcon />} value="fixedprices">
                        {translate('invoiceapproval.fixedprices.title', {
                            selectedCount: values.fixedPrices?.length || 0,
                            count: fixedPricesCount,
                        })}
                    </Tab>
                </TabList>
                <div>
                    <ViewInvoiceApprovalTimes
                        hiddenClassName={selectedTab !== 'times' && styles.hidden}
                        {...componentProps}
                        setTimesCount={setTimesCount}
                        setTimes={setTimes}
                        selectedTimes={selectedTimes}
                    />
                    <ViewInvoiceApprovalTravels
                        hiddenClassName={selectedTab !== 'travels' && styles.hidden}
                        {...componentProps}
                        setTravelsCount={setTravelsCount}
                        setTravels={setTravels}
                        selectedTravels={selectedTravels}
                    />
                    <ViewInvoiceApprovalDisbursements
                        hiddenClassName={selectedTab !== 'disbursements' && styles.hidden}
                        {...componentProps}
                        setDisbursementsCount={setDisbursementsCount}
                        setDisbursements={setDisbursements}
                        selectedDisbursements={selectedDisbursements}
                    />
                    <ViewInvoiceApprovalFixedPrices
                        hiddenClassName={selectedTab !== 'fixedprices' && styles.hidden}
                        {...componentProps}
                        setFixedPricesCount={setFixedPricesCount}
                        setFixedPrices={setFixedPrices}
                        selectedFixedPrices={selectedFixedPrices}
                    />
                </div>
            </Card>
        </Form>
    )
}

export default function ViewInvoiceApproval(): ReactElement {
    const axios = useAxios()
    const history = useHistory()
    const { id } = useParams<{ id?: string }>()
    const { t: translate } = useTranslation()
    const { currentUser } = useUser()
    const { isViewport, viewport } = useViewport()

    const headerOrientation = useMemo(() => (isViewport('<', Viewport.Large) ? 'vertical' : 'horizontal'), [viewport])
    const orientation = useMemo(() => (isViewport('<', Viewport.MainMenu) ? 'vertical' : 'horizontal'), [viewport])

    const [approval] = useEntity<InvoiceApprovalDto>((id: string) => ({ url: `/finance/invoice-approval/${id}`, method: 'GET' }), id)

    if (id && approval.loading) {
        return <></>
    }

    return (
        <Formik<InvoiceApprovalMutationDto>
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={getValidationSchema(translate)}
            initialValues={{
                id: approval.data?.id,
                userId: approval.data?.userId || currentUser?.id,
                fileIds: approval.data?.files?.map(file => file.id) || [],
                clientId: approval.data?.clientId,
                debtorId: approval.data?.debtorId,
                invoiceEmail: approval.data?.invoiceEmail,
                invoiceReference: approval.data?.invoiceReference,
                userApprovals: approval.data?.userApprovals || [],
                disbursements: approval.data?.disbursements || [],
                fixedPrices: approval.data?.fixedPrices || [],
                times: approval.data?.times || [],
                travels: approval.data?.travels || [],
            }}
            onSubmit={(values, formikHelpers) => {
                if (values.id) {
                    axios.put(`/finance/invoice-approval/${values.id}`, values).finally(() => {
                        formikHelpers.setSubmitting(false)
                        formikHelpers.resetForm({ values: values })
                    })
                } else {
                    axios
                        .post('/finance/invoice-approval', values)
                        .then(() => history.push(`/invoice-approval`))
                        .finally(() => {
                            formikHelpers.setSubmitting(false)
                        })
                }
            }}
        >
            <Container approval={approval.data} headerOrientation={headerOrientation} orientation={orientation} />
        </Formik>
    )
}
